import React from 'react';
import { Tour } from 'antd';
import type { TourProps } from 'antd';
import SagaRaven, { SAGA_SINGLETON_PRIORITIES } from '../SagaRaven';
import { isTutorialUiSuppressionRequested } from '../utils/tutorialMode';
import './AppTour.css';

export type AppTourPanelProps = {
  children: React.ReactNode;
  kicker?: string;
  status?: React.ReactNode;
  ravenSize?: 'sm' | 'md' | 'lg';
  className?: string;
};

export type AppTourVideoLinkProps = {
  href: string;
  children?: React.ReactNode;
  newTab?: boolean;
};

const ravenSizeClass = {
  sm: 'app-tour-raven--sm',
  md: 'app-tour-raven--md',
  lg: 'app-tour-raven--lg',
};

export const AppTourPanel: React.FC<AppTourPanelProps> = ({
  children,
  kicker = 'Guide',
  status,
  ravenSize = 'md',
  className,
}) => (
  <div className={`app-tour-panel${className ? ` ${className}` : ''}`}>
    <div className={`app-tour-raven ${ravenSizeClass[ravenSize]}`} aria-hidden="true">
      <SagaRaven animation="teacher" priority={SAGA_SINGLETON_PRIORITIES.tour} style={{ width: '100%', height: '100%' }} />
    </div>
    <div className="app-tour-copy">
      <div className="app-tour-copy-header">
        <span className="app-tour-kicker">{kicker}</span>
        {status ? <span className="app-tour-status">{status}</span> : null}
      </div>
      <div className="app-tour-body">{children}</div>
    </div>
  </div>
);

export const renderAppTourPanel = (
  content: React.ReactNode,
  options?: Omit<AppTourPanelProps, 'children'>
) => <AppTourPanel {...options}>{content}</AppTourPanel>;

export const AppTourVideoLink: React.FC<AppTourVideoLinkProps> = ({
  href,
  children = 'Watch the video tutorial',
  newTab = true,
}) => (
  <a
    className="app-tour-video-link"
    href={href}
    target={newTab ? '_blank' : undefined}
    rel={newTab ? 'noopener noreferrer' : undefined}
  >
    {children}
  </a>
);

export type AppTourProps = TourProps;

export const AppTour: React.FC<AppTourProps> = ({ rootClassName, ...props }) => {
  if (!Tour || isTutorialUiSuppressionRequested()) {
    return null;
  }
  return (
    <Tour
      {...props}
      rootClassName={['app-tour', rootClassName].filter(Boolean).join(' ')}
    />
  );
};

export default AppTour;
